import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { ApCnList } from "@feature/types/apcnlist.type"
import { WOSChargeLineEntity } from '@feature/types/woschargelineentity';
import { ApInvoiceEntity } from '@feature/types/apinvoiceentity';
import { ENV } from "@app/constants/env";
import { getToken } from '@app/helpers/authProvider';
import { ApCreditNote } from '@feature/types/apcreditnote';
import { arcncopychargeline, ARInvoiceSummary } from '@feature/types/arinvoicesummery.type';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.BASE_API_URL +ENV.FINANCE_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken(); // ensure this returns a non-empty string
    if (token) {
      const headers =
        (args as FetchArgs).headers instanceof Headers
          ? (args as FetchArgs).headers
          : new Headers((args as FetchArgs).headers);

      headers.set('Authorization', `Bearer ${token}`); // use standard casing
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    // optional: retry once if unauthorized (adjust getToken refresh logic if available)
    if (result.error && result.error.status === 401 && token) {
      // If you have a refresh flow, invoke it here then retry.
      // const refreshed = await getRefreshedToken();
      // if (refreshed) { headers.set('Authorization', `Bearer ${refreshed}`); return rawBaseQuery(args, api, extraOptions); }
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

export interface ApiResponse<T> {
  success: boolean
  message?: string
  data?: T
  error?: string
}





export const apcnsliceapi = createApi({
  reducerPath: 'apcnsliceApi',
  baseQuery: authBaseQuery,
  tagTypes: ['finance'],
  endpoints: (builder) => ({

   

   getapcndatalist: builder.query<ApiResponse<ApCnList>, { showID: number; loggedInUserId: number }>({
      query: ({ showID, loggedInUserId }) => ({
        url: `ApCnFinanceReport/GetAPCNDataList/${showID}/${loggedInUserId || 0}`,
        method: 'POST',
      }),
      providesTags: ['finance'],
    }),
   
  getSupplierInvoiceDetailsByWoid: builder.query<ApiResponse<any>, number>({
    query: (woId) => ({
    url: `ApInvoice/GetSupplierInvoiceDetailsByWoid/${woId}`,
    method: 'GET',
    }),
    providesTags: ['finance'],
  }),

    // Upload Excel file(s) for AP Web Cost Transactions
    uploadApWebCostTransactions: builder.mutation<ApiResponse<any>, FormData>({
      query: (formData) => ({
        url: 'ApInvoice/UploadApWebCostTransactions',
        method: 'POST',
        body: formData,       
      }),
      invalidatesTags: ['finance'],
    }),
 // Get AP Web Cost Transactions By AP Invoice Id
getApWebCostTransactionsByApInvoiceId: builder.query<ApiResponse<any>, number>({
  query: (apInvoiceId) => ({
    url: `ApInvoice/GetAPWebCostTransactionsByAPInvoiceId/${apInvoiceId}`,
    method: 'GET',
  }),
  providesTags: ['finance'],
}),


saveUploadedAPWebCostFile: builder.mutation<
  ApiResponse<number>,
  {
    fileName: string;
    filePath: string;
    fileSize: string;
    totalRecords?: number | null;
    processedRecords?: number | null;
    failedRecords?: number | null;
    remarks: string;
    createdBy: string;
  }
>({
  query: (body) => ({
    url: 'ApInvoice/SaveUploadedAPWebCostFile',
    method: 'POST',
    body,
  }),
  invalidatesTags: ['finance'],
}),
getUploadedAPWebCostFiles: builder.query<ApiResponse<any>, void>({
  query: () => ({
    url: 'ApInvoice/GetUploadedAPWebCostFiles',
    method: 'GET',
  }),
  providesTags: ['finance'],
}),
  getSupplierInvoiceDetailsByInvoiceid: builder.query<ApiResponse<any>, { WoId: number; Invoiceid: number }>({
    query: ({ WoId, Invoiceid }) => ({
      url: `ApInvoice/GetSupplierInvoiceDetailsByInvoiceid/${WoId}/${Invoiceid}`,
      method: 'GET',
    }),
    providesTags: ['finance'],
  }),
  getAPWebCostDetailsByUserIdCompany: builder.query<ApiResponse<any>, { userId: number }>({
  query: ({ userId }) => ({
    url: `ApInvoice/GetAPWebCostDetailsByUserIdCompany/${userId}`,
    method: 'GET',
  }),
  providesTags: ['finance'],
}),

   getapinvoicecharge: builder.query<ApiResponse<WOSChargeLineEntity>, { invoiceid: number; supplierId: number; WOID: number }>({
        query: ({ invoiceid, supplierId, WOID }) => ({
          url: `WosChargeLine/GetAPInvoiceChargeLine/${invoiceid}/${supplierId}/${WOID}`,
          method: 'GET',
        }),
        providesTags: ['finance'],
      }),

       createchargelinefromapinvoice: builder.mutation<ApiResponse<WOSChargeLineEntity>, WOSChargeLineEntity>({
          query: (addchargeline) => ({
            url: 'WosChargeLine/CreateChargeLineFromAPInvoice',
            method: 'POST',
            body: addchargeline,
          }),
          invalidatesTags: ['finance'],
        }),


         saveapinvoice: builder.mutation<ApiResponse<ApInvoiceEntity>, ApInvoiceEntity>({
            query: (saveapinvoice) => ({
              url: 'ApInvoice/SaveAPInvoice',
              method: 'POST',
              body: saveapinvoice,
            }),
            invalidatesTags: ['finance'],
          }),
        
        
          submittedapinvwithoutdraft: builder.mutation<ApiResponse<any>, ApInvoiceEntity>({
            query: (savearinvoice) => ({
              url: 'ApInvoice/SubmitForApprovalWithoutDraftAsync',
              method: 'POST',
              body: savearinvoice,
            }),
            invalidatesTags: ['finance'],
          }),
        
          submittedapinvoice: builder.mutation<ApiResponse<any>, ApInvoiceEntity>({
            query: (submittedapinv) => ({
              url: 'ApInvoice/SubmitForApprovalAsync',
              method: 'PUT',
              body: submittedapinv,
            }),
            invalidatesTags: ['finance'],
          }),
        
        
          approveapinvoice: builder.mutation<ApiResponse<any>, ApInvoiceEntity>({
            query: (approveentity) => ({
              url: 'ApInvoice/ApproveAPInvoice',
              method: 'PUT',
              body: approveentity,
            }),
            invalidatesTags: ['finance'],
          }),
          
          rejectapinvoice: builder.mutation<ApiResponse<any>, ApInvoiceEntity>({
            query: (rejectinv) => ({
              url: 'ApInvoice/RejectAPInvoice',
              method: 'PUT',
              body: rejectinv,
            }),
            invalidatesTags: ['finance'],
          }),
          cancelapinvoice: builder.mutation<ApiResponse<any>, ApInvoiceEntity>({
            query: (cancelapinv) => ({
              url: 'ApInvoice/CancelAPInvoice',
              method: 'PUT',
              body: cancelapinv,
            }),
            invalidatesTags: ['finance'],
          }),
        
          getwosupplierinvoiceaudit: builder.query<ApiResponse<any>, number>({
            query: (Invoiceid) => ({
              url: `ApInvoice/GetWOSupplierInvoiceAudit/${Invoiceid}`,
              method: 'GET',
            }),
            providesTags: ['finance'],
          }),


          getapcndetailsbyrefinvid: builder.query<ApiResponse<ApCreditNote>, number>({
            query: (RefInvoiceid) => ({
              url: `ApInvoice/GetAPCNDetailsByRefInvid/${RefInvoiceid}`,
              method: 'GET',
            }),
            providesTags: ['finance'],
          }),

          getapcndetailsbyinvoiceid: builder.query<ApiResponse<ApCreditNote>, number>({
            query: (CNID) => ({
              url: `ApInvoice/GetAPCNDetailsByInvoiceid/${CNID}`,
              method: 'GET',
            }),
            providesTags: ['finance'],
          }),

          saveapcninvoice: builder.mutation<ApiResponse<ApCreditNote>, ApCreditNote>({
            query: (saveapcninvoice) => ({
              url: 'ApInvoice/SaveAPCNInvoice',
              method: 'POST',
              body: saveapcninvoice,
            }),
            invalidatesTags: ['finance'],
          }),


          submitapinvoicecn: builder.mutation<ApiResponse<ApCreditNote>, ApCreditNote>({
            query: (submitdto) => ({
              url: 'ApInvoice/SubmitAPInvoiceCNAsync',
              method: 'PUT',
              body: submitdto,
            }),
            invalidatesTags: ['finance'],
          }),

          approveapinvoicecn: builder.mutation<ApiResponse<ApCreditNote>, ApCreditNote>({
            query: (approvedto) => ({
              url: 'ApInvoice/ApproveAPInvoiceCNAsync',
              method: 'PUT',
              body: approvedto,
            }),
            invalidatesTags: ['finance'],
          }),

          rejectapinvoicecn: builder.mutation<ApiResponse<ApCreditNote>, ApCreditNote>({
            query: (rejectdto) => ({
              url: 'ApInvoice/RejectAPInvoiceCNAsync',
              method: 'PUT',
              body: rejectdto,
            }),
            invalidatesTags: ['finance'],
          }),

          cancelapinvoicecn: builder.mutation<ApiResponse<ApCreditNote>, ApCreditNote>({
            query: (canceldto) => ({
              url: 'ApInvoice/CancelAPInvoiceCNAsync',
              method: 'PUT',
              body: canceldto,
            }),
            invalidatesTags: ['finance'],
          }),


          getapcnchargelines: builder.query<ApiResponse<WOSChargeLineEntity[]>, { invoiceid: number; CNID: number }>({
            query: ({ invoiceid, CNID }) => ({
              url: `WosChargeLine/GetAPCNChargeLines/${invoiceid}/${CNID}`,
              method: 'GET',
            }),
            providesTags: ['finance'],
          }),

          getapinvoicesummery: builder.query<ApiResponse<ARInvoiceSummary>, number>({
            query: (Invoiceid) => ({
              url: `ApInvoice/GetAPInvoiceSummery/${Invoiceid}`,
              method: 'GET',
            }),
            providesTags: ['finance'],
          }),

          copyapchargelinetoapcn: builder.mutation<ApiResponse<arcncopychargeline>, arcncopychargeline>({
              query: (addchargeline) => ({
                url: 'WosChargeLine/CopyAPChargeLinesToAPCN',
                method: 'POST',
                body: addchargeline,
              }),
              invalidatesTags: ['finance'],
            }),

            getapwocreditnoteaudit: builder.query<ApiResponse<any>, number>({
              query: (Invoiceid) => ({
                url: `ApCnFinanceReport/GetApWOCreditNoteAudit/${Invoiceid}`,
                method: 'POST',
              }),
              providesTags: ['finance'],
            }),

              saveapcninvoicedraft: builder.mutation<ApiResponse<ApCreditNote>, ApCreditNote>({
            query: (saveapcninvoice) => ({
              url: 'ApInvoice/SaveAPCNInvoiceDraft',
              method: 'POST',
              body: saveapcninvoice,
            }),
            invalidatesTags: ['finance'],
          }),

  }),
});





export const {
  useGetapcndatalistQuery,
  useGetSupplierInvoiceDetailsByWoidQuery,
  useGetSupplierInvoiceDetailsByInvoiceidQuery,
  useGetapinvoicechargeQuery,
  useCreatechargelinefromapinvoiceMutation,
  useSaveapinvoiceMutation,
  useSubmittedapinvwithoutdraftMutation,
  useSubmittedapinvoiceMutation,
  useApproveapinvoiceMutation,
  useRejectapinvoiceMutation,
  useCancelapinvoiceMutation,
  useGetwosupplierinvoiceauditQuery,
  useGetapcndetailsbyrefinvidQuery,
  useGetapcndetailsbyinvoiceidQuery,
  useSaveapcninvoiceMutation,
  useSubmitapinvoicecnMutation,
  useApproveapinvoicecnMutation,
  useRejectapinvoicecnMutation,
  useCancelapinvoicecnMutation,
  useGetapcnchargelinesQuery,
  useGetapinvoicesummeryQuery,
  useCopyapchargelinetoapcnMutation,
  useGetapwocreditnoteauditQuery,
  useSaveapcninvoicedraftMutation,
  useUploadApWebCostTransactionsMutation,
  useGetApWebCostTransactionsByApInvoiceIdQuery,
  useSaveUploadedAPWebCostFileMutation,
  useGetUploadedAPWebCostFilesQuery,
  useGetAPWebCostDetailsByUserIdCompanyQuery
} = apcnsliceapi;
